import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BarChart2, Clock, Zap, FilePlus, FileText, Bell } from "lucide-react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "../styles/Dashboard.css";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Dummy data for recent activities
const dummyRecentActivities = [
  { id: 1, action: "Created", certificate: "Flanschmonteur - John Doe", date: "2025-06-03" },
  { id: 2, action: "Modified", certificate: "PSAgA - Jane Smith", date: "2025-06-02" },
  { id: 3, action: "Revoked", certificate: "Hubarbeitsbühnen - Alice Brown", date: "2025-06-01" },
];

// Dummy data for summary stats
const summaryStats = {
  totalIssued: 150,
  revoked: 5,
  reachingExpiry: 10,
};

// Chart data with explicit colors for light and dark modes
const getChartData = (isDarkMode) => ({
  labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  datasets: [
    {
      label: "Zertifikate ausgestellt",
      data: [20, 25, 30, 15, 35, 25],
      backgroundColor: isDarkMode ? "#1a8c92" : "#167378", // Explicit colors for dark/light mode
      borderColor: isDarkMode ? "#167378" : "#125e60",
      borderWidth: 1,
    },
  ],
});

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    }
  }, [navigate]);

  const chartOptions = {
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t("dashboard.chartYLabel"),
          color: "var(--text-primary)",
        },
        ticks: {
          color: "var(--text-secondary)",
        },
      },
      x: {
        title: {
          display: true,
          text: t("dashboard.chartXLabel"),
          color: "var(--text-primary)",
        },
        ticks: {
          color: "var(--text-secondary)",
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          color: "var(--text-primary)",
        },
      },
      title: {
        display: true,
        text: t("dashboard.chartTitle"),
        color: "var(--text-primary)",
      },
    },
  };

  const handleCreateCertificate = () => {
    navigate("/create-certificate");
  };

  const handleViewReports = () => {
    navigate("/manage-certificates");
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Header Section */}
        <header className="dashboard-header">
          <h1>{t("app.title")}</h1>
        </header>

        {/* Main Content */}
        <main className="dashboard-main">
          {/* Overview Section */}
          <section className="overview-section animate-fade-in">
            <h2>
              <BarChart2 size={24} className="section-icon" />
              {t("dashboard.overview")}
            </h2>
            <div className="stats-container">
              <div className="stat-card animate-hover">
                <h3>{summaryStats.totalIssued}</h3>
                <p>{t("dashboard.totalIssued")}</p>
              </div>
              <div className="stat-card animate-hover">
                <h3>{summaryStats.revoked}</h3>
                <p>{t("dashboard.revoked")}</p>
              </div>
              <div className="stat-card animate-hover">
                <h3>{summaryStats.reachingExpiry}</h3>
                <p>{t("dashboard.reachingExpiry")}</p>
              </div>
            </div>
            {/* Chart */}
            <div className="chart-container">
              <Bar
                data={{
                  ...getChartData(isDarkMode),
                  datasets: [
                    {
                      ...getChartData(isDarkMode).datasets[0],
                      label: t("dashboard.chartTitle"),
                    },
                  ],
                }}
                options={chartOptions}
                height={250}
              />
            </div>
          </section>

          <div className="dashboard-grid">
            {/* Recent Activity Section */}
            <section className="recent-activity-section animate-fade-in">
              <h2>
                <Clock size={24} className="section-icon" />
                {t("dashboard.recentActivity")}
              </h2>
              <ul className="activity-list">
                {dummyRecentActivities.map((activity) => (
                  <li key={activity.id} className="activity-item animate-hover">
                    <span>{activity.action}</span> - <strong>{activity.certificate}</strong> am {activity.date}
                  </li>
                ))}
              </ul>
            </section>

            {/* Quick Actions and Notifications Section */}
            <div className="side-sections">
              {/* Quick Actions Section */}
              <section className="quick-actions-section animate-fade-in">
                <h2>
                  <Zap size={24} className="section-icon" />
                  {t("dashboard.quickActions")}
                </h2>
                <div className="action-buttons">
                  <button className="action-btn animate-hover" onClick={handleCreateCertificate}>
                    <FilePlus size={20} className="btn-icon" />
                    {t("dashboard.createCertificate")}
                  </button>
                  <button className="action-btn animate-hover" onClick={handleViewReports}>
                    <FileText size={20} className="btn-icon" />
                    {t("dashboard.viewReports")}
                  </button>
                </div>
              </section>

              {/* Notifications Section */}
              <section className="notifications-section animate-fade-in">
                <h2>
                  <Bell size={24} className="section-icon" />
                  {t("dashboard.notifications")}
                </h2>
                <div className="notification animate-hover">
                  <p>{t("dashboard.notification1")}</p>
                </div>
                <div className="notification animate-hover">
                  <p>{t("dashboard.notification2")}</p>
                </div>
              </section>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;